import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthAppleButton } from 'components/Auth';
import useTracking from 'hooks/useTracking';
import useTranslate from 'hooks/useTranslate';
import useGoogleTagManagerTrack from 'hooks/useGoogleTagManagerTrack';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { AuthenticateGrantType, AuthenticateProvider } from 'constants/auth';
import { CODE_LOGIN, LOGIN_WITHOUT_EMAIL_URL } from 'constants/routes';
import { GoogleTagManagerEvent } from 'constants/google';
import { actions } from 'state/auth/slice';
import { AuthExternalRegisterView } from 'state/auth/constants';
import { authenticateFailEvent, authenticateSuccessEvent, clickEvent } from 'libs/common/event-tracker/events';
import { urlWithParams } from 'libs/utils/url';
import { getFingerprint } from 'libs/utils/fingerprint';
import { navigateToPage } from 'libs/utils/window';
import { authenticateUser } from 'data/api/account';
import { ResponseCode } from 'data/api/response-codes';
import { getCurrentUser } from 'data/api';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import useSuccessUrl from '../hooks/useSuccessUrl';
import { jsx as _jsx } from "react/jsx-runtime";

const Apple = _ref => {
  let {
    setError
  } = _ref;
  const {
    track
  } = useTracking();
  const translate = useTranslate();
  const dispatch = useDispatch();
  const isLoginWithoutEmailInModalEnabled = useSelector(getIsFeatureSwitchEnabled('login_without_email_in_modal'));
  const {
    googleTagManagerTrack
  } = useGoogleTagManagerTrack();
  const {
    0: isLoading,
    1: setIsLoading
  } = useState(false);
  const successUrl = useSuccessUrl();

  function handleClick() {
    track(clickEvent({
      target: ClickableElement.LoginWithApple
    }));
  }

  function trackError(message) {
    track(authenticateFailEvent({
      type: 'apple',
      error: message
    }));
  }

  function setExternalRegisterData(idToken, user) {
    dispatch(actions.setExternalRegisterData({
      view: AuthExternalRegisterView.AppleRegister,
      data: {
        idToken,
        realName: (user === null || user === void 0 ? void 0 : user.name) && "".concat(user.name.firstName, " ").concat(user.name.lastName),
        email: user === null || user === void 0 ? void 0 : user.email
      }
    }));
  }

  function handleLoginWithoutEmail(controlCode, email) {
    if (isLoginWithoutEmailInModalEnabled) {
      dispatch(actions.setExternalRegisterData({
        view: AuthExternalRegisterView.PasswordVerification,
        data: {
          idToken: controlCode,
          email
        }
      }));
      return;
    }

    navigateToPage(urlWithParams(LOGIN_WITHOUT_EMAIL_URL, {
      control_code: controlCode
    }));
  }

  async function handleSuccess(_ref2) {
    let {
      authorization: {
        id_token
      },
      user
    } = _ref2;
    setIsLoading(true);
    const fingerprint = await getFingerprint();
    const response = await authenticateUser({
      grantType: AuthenticateGrantType.Assertion,
      provider: AuthenticateProvider.Apple,
      assertion: id_token,
      fingerprint
    });

    if ('errors' in response) {
      var _response$payload;

      // TODO: handle ResponseCode.SessionFromTokenError
      if (response.code === ResponseCode.NotFound || response.code === ResponseCode.ValidationError) {
        setExternalRegisterData(id_token, user);
      } else if (response.code === ResponseCode.LoginWithoutEmail && (_response$payload = response.payload) !== null && _response$payload !== void 0 && _response$payload.control_code) {
        handleLoginWithoutEmail(response.payload.control_code, user === null || user === void 0 ? void 0 : user.email);
      } else if (response.code === ResponseCode.Required2FA && response.payload) {
        navigateToPage(CODE_LOGIN({
          refUrl: successUrl,
          authProps: response.payload
        }));
      } else {
        setError(response.message);
        setIsLoading(false);
      }

      return;
    }

    const getCurrentUserResp = await getCurrentUser();
    const userId = 'errors' in getCurrentUserResp ? undefined : getCurrentUserResp.user.id;
    googleTagManagerTrack(GoogleTagManagerEvent.Login, {
      auth_type: 'apple',
      user_email: user === null || user === void 0 ? void 0 : user.email
    });
    track(authenticateSuccessEvent({
      type: 'apple',
      userId
    }));
    navigateToPage(successUrl);
  }

  function handleFailure() {
    trackError('User closed screen or did not provide permissions');
  }

  return /*#__PURE__*/_jsx(AuthAppleButton, {
    text: translate('auth.select_type.actions.apple'),
    isLoading: isLoading,
    onClick: handleClick,
    onSuccess: handleSuccess,
    onFailure: handleFailure
  });
};

export default Apple;